import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { usersAPI } from "@/api";
import { setAlert } from '@store/alerts.reducer';

interface BalanceState {
  value: number;
  balanceId: string | null;
  requestedBalanceId: string | null;
  valuesByBalanceId: Record<string, number>;
  loadingByBalanceId: Record<string, boolean>;
  errorsByBalanceId: Record<string, boolean>;
  refreshVersion: number;
  loading: boolean;
  error: boolean;
}

const initialState: BalanceState = {
  value: 0,
  balanceId: null,
  requestedBalanceId: null,
  valuesByBalanceId: {},
  loadingByBalanceId: {},
  errorsByBalanceId: {},
  refreshVersion: 0,
  loading: false,
  error: false
};

const showErrorAlert = (dispatch: any) => {
    dispatch(setAlert({
      title: 'Ошибка',
      text: 'Не удалось загрузить баланс доп. активностей',
      type: 'error'
    }));
};

// Асинхронный thunk для получения баланса
export const fetchBalanceOfAdditionalActivities = createAsyncThunk(
  'balanceOfAdditionalActivities/fetchBalanceOfAdditionalActivities',
  async (
    params: string | number | { user_id: string | number; balance_id?: string | number | null },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const requestParams =
        typeof params === 'object'
          ? params
          : { user_id: params };
      const balanceId = String(
        typeof params === 'object' ? params.balance_id ?? '0' : '0'
      );
      const { data } = await usersAPI.getBalanceOfAdditionalActivities(requestParams);
      if (data.status === 'success') {
        return {
          value: data?.data ?? 0,
          balanceId,
        };
      } else {
        showErrorAlert(dispatch)
        return rejectWithValue('Error fetching balance');
      }
    } catch (error) {
      showErrorAlert(dispatch)
      return rejectWithValue('Error fetching balance');
    }
  }
);

const balanceOfAdditionalActivitiesSlise = createSlice({
  name: 'balanceOfAdditionalActivitiesSlise',
  initialState,
  reducers: {
    setBalance: (state, action: PayloadAction<number>) => {
      state.value = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBalanceOfAdditionalActivities.pending, (state, action) => {
        const balanceId = String(
          typeof action.meta.arg === 'object'
            ? action.meta.arg.balance_id ?? '0'
            : '0'
        );
        state.requestedBalanceId = balanceId;
        state.loadingByBalanceId[balanceId] = true;
        state.errorsByBalanceId[balanceId] = false;
        state.loading = true;
        state.error = false;
      })
      .addCase(fetchBalanceOfAdditionalActivities.fulfilled, (state, action) => {
        state.refreshVersion += 1;
        state.valuesByBalanceId[action.payload.balanceId] = action.payload.value;
        state.loadingByBalanceId[action.payload.balanceId] = false;
        state.errorsByBalanceId[action.payload.balanceId] = false;
        if (state.requestedBalanceId === action.payload.balanceId) {
          state.value = action.payload.value;
          state.balanceId = action.payload.balanceId;
          state.loading = false;
          state.error = false;
        }
      })
      .addCase(fetchBalanceOfAdditionalActivities.rejected, (state, action) => {
        const rejectedBalanceId = String(
          typeof action.meta.arg === 'object'
            ? action.meta.arg.balance_id ?? '0'
            : '0'
        );
        state.loadingByBalanceId[rejectedBalanceId] = false;
        state.errorsByBalanceId[rejectedBalanceId] = true;
        if (state.requestedBalanceId === rejectedBalanceId) {
          state.loading = false;
          state.error = true;
        }
      });
  }
});

export const { setBalance } = balanceOfAdditionalActivitiesSlise.actions;
export default balanceOfAdditionalActivitiesSlise.reducer;
